import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SiteSettings {
  id: string;
  page_contact_visible: boolean;
  page_instruments_visible: boolean;
  page_atelier_visible: boolean;
  maintenance_mode: boolean;
  maintenance_message_en: string;
  maintenance_message_de: string;
  seo_title_en: string;
  seo_title_de: string;
  seo_description_en: string;
  seo_description_de: string;
  seo_og_image_path: string | null;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_street: string;
  contact_postal_code: string;
  contact_city: string;
  contact_country: string;
  notification_email: string;
  default_reply_to_email: string;
  default_from_name: string;
  send_customer_confirmation: boolean;
  send_admin_notification: boolean;
  auto_translate_inquiries: boolean;
  show_contact_details: boolean;
  show_contact_on_contact_page: boolean;
  show_contact_in_footer: boolean;
  page_impressum_visible: boolean;
  page_datenschutz_visible: boolean;
  section_brand_statement_visible: boolean;
  social_instagram: string;
  social_facebook: string;
  social_youtube: string;
  social_website: string;
  hero_image_path: string | null;
  logo_path: string | null;
  gallery_viewer_height_vh: number;
  instruments_view_mode: string;
  instruments_show_description: boolean;
  instruments_list_image_size: string;
  instruments_gallery_columns: number;
  instruments_gallery_text_position: string;
  section_featured_visible: boolean;
  section_atelier_teaser_visible: boolean;
  section_contact_teaser_visible: boolean;
  homepage_hero_cta_visible: boolean;
  footer_copyright_text: string;
  updated_at: string;
}

const defaultSettings: SiteSettings = {
  id: '',
  page_contact_visible: true,
  page_instruments_visible: false,
  page_atelier_visible: false,
  maintenance_mode: false,
  maintenance_message_en: '',
  maintenance_message_de: '',
  seo_title_en: '',
  seo_title_de: '',
  seo_description_en: '',
  seo_description_de: '',
  seo_og_image_path: null,
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_street: '',
  contact_postal_code: '',
  contact_city: '',
  contact_country: '',
  notification_email: '',
  default_reply_to_email: '',
  default_from_name: 'Szoradi Instruments',
  send_customer_confirmation: true,
  send_admin_notification: true,
  auto_translate_inquiries: true,
  show_contact_details: false,
  show_contact_on_contact_page: false,
  show_contact_in_footer: false,
  page_impressum_visible: true,
  page_datenschutz_visible: true,
  section_brand_statement_visible: true,
  social_instagram: '',
  social_facebook: '',
  social_youtube: '',
  social_website: '',
  hero_image_path: null,
  logo_path: null,
  gallery_viewer_height_vh: 75,
  instruments_view_mode: 'gallery',
  instruments_show_description: true,
  instruments_list_image_size: 'medium',
  instruments_gallery_columns: 2,
  instruments_gallery_text_position: 'below',
  section_featured_visible: true,
  section_atelier_teaser_visible: true,
  section_contact_teaser_visible: true,
  homepage_hero_cta_visible: true,
  footer_copyright_text: '© {year} Szoradi Instruments',
  updated_at: '',
};

interface SiteSettingsContextType {
  settings: SiteSettings;
  loading: boolean;
  refetch: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  settings: defaultSettings,
  loading: true,
  refetch: async () => {},
});

export const SiteSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('site_settings' as any)
      .select('*')
      .limit(1)
      .single();
    if (data) {
      setSettings(data as unknown as SiteSettings);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refetch: fetchSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
